import { Injectable } from "@angular/core";
import { jwtDecode, JwtPayload } from 'jwt-decode';

const localStorageTokenKey = 'user_token';

interface DecodedToken extends JwtPayload {
  sub?: string;
  email?: string;
  roles?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class JwtTokenService {

  public getJwtToken(): string | null {
    return localStorage.getItem(localStorageTokenKey);
  }

  public getJwtTokenIfValid(): string | null {
    var token =  localStorage.getItem(localStorageTokenKey);
    return this.isTokenExpired(token) ? null : token;
  }

  public setJwtToken(token: string): void {
    localStorage.setItem(localStorageTokenKey, token);
  }

  public removeJwtToken(): void {
    localStorage.removeItem(localStorageTokenKey);
  }

  public setAccessToken(token: string): void { 
    /* quasi duplicate von setJwtToken ; TODO: nach fertigstellung refactorn */
    localStorage.setItem(localStorageTokenKey, token);
  }

  /**
   * Refresh Token ist HttpOnly Cookie - wird automatisch vom Browser verwaltet!
   */
  public clearTokens(): void {
    this.removeJwtToken();
    // Refresh Token Cookie wird vom Server beim Logout gelöscht
    
    // ✅ NEU: Auch Session-ID löschen
    localStorage.removeItem('session-id');
  }

  /**
   * Dekodiert das JWT und gibt die Payload zurück
   */
  public decodeToken(token: string | null): DecodedToken | null {
    if (!token) return null;

    try {
      return jwtDecode<DecodedToken>(token);
    } catch (error) {
      /* console.error('Error decoding token:', error); */
      return null;
    }
  }

  /**
   * Prüft, ob das Token abgelaufen ist
   */
  public isTokenExpired(token: string | null): boolean {
    if (!token) return true;

    const decoded = this.decodeToken(token);
    if (!decoded || !decoded.exp) return true;

    // exp ist in Sekunden, Date.now() in Millisekunden
    const expirationTime = decoded.exp * 1000;
    return Date.now() >= expirationTime;
  }

  /**
   * Gibt die verbleibende Zeit bis Ablauf in Millisekunden zurück
   */
  public getTokenExpirationTime(token: string | null): number {
    if (!token) return 0;

    const decoded = this.decodeToken(token);
    if (!decoded || !decoded.exp) return 0;

    const expirationTime = decoded.exp * 1000;
    const timeLeft = expirationTime - Date.now();

    return timeLeft > 0 ? timeLeft : 0;
  }

  /**
   * Prüft, ob das Token bald abläuft (z.B. in weniger als 1 Minute)
   */
  public isTokenExpiringSoon(token: string | null, thresholdMs: number = 60000): boolean {
    const timeLeft = this.getTokenExpirationTime(token);
/*     console.log(`Token expires in ${timeLeft} ms`);
 */    const timeLeftInHms = timeLeft / 60000;
/*     console.log(`Token expires in ${timeLeftInHms} minutes`);
 */    let decodedToken = this.decodeToken(token);
/*     console.log(`Decoded token: ${JSON.stringify(decodedToken)}`);
 */    return timeLeft > 0 && timeLeft < thresholdMs;
  }

  /**
   * Gibt die aktuell gespeicherte Token-Payload zurück
   */
  public getCurrentTokenPayload(): DecodedToken | null {
    const token = this.getJwtToken();
    /* console.log(`Current token payload: ${JSON.stringify(this.decodeToken(token))}`); */
    return this.decodeToken(token);
  }

  /**
   * Prüft, ob ein Token gültig ist (nicht abgelaufen)
   */
  public isTokenValid(token: string | null): boolean {
    if (!token) return false;
    return !this.isTokenExpired(token);
  }

  public hasRefreshTokenCookie(): boolean {
    // Das können wir nicht direkt aus JS auslesen (HttpOnly!)
    // aber wir können beim Refresh prüfen, ob der Server einen neuen setzt
    return true; // Vertraue dem Server
  }

  /**
   * ✅ NEU: Formatiert die verbleibende Zeit in lesbarem Format
   */
  public getTokenExpirationFormatted(token: string | null): string {
    const timeLeft = this.getTokenExpirationTime(token);
    
    if (timeLeft <= 0) return 'Abgelaufen';
    
    const minutes = Math.floor(timeLeft / 60000);
    const seconds = Math.floor((timeLeft % 60000) / 1000);
    
    return `${minutes}m ${seconds}s`;
  }

  /**
   * ✅ NEU: Gibt Ablauf-Zeitpunkt als Date zurück
   */
  public getTokenExpirationDate(token: string | null): Date | null {
    if (!token) return null;

    const decoded = this.decodeToken(token);
    if (!decoded || !decoded.exp) return null;

    return new Date(decoded.exp * 1000);
  }
}